import { ForbiddenAlertIcon } from "@images/svgIcons/forbiddenAlertIcon"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import { StripeErrorProgramBundleCardSettingsButton } from "@modules/finances/components/stripeErrorButtons"
import { getPaymentErrorContent, getPaymentErrorSubtitle } from "@modules/finances/hooks/useStripeAccountErrors"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleSubscriptionActions } from "@modules/programBundle/components/programBundleSubscriptionActions"
import { ProgramBundleTrialCard } from "@modules/programBundle/components/programBundleTrialCard"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundlePriceCardProps = {
  programBundle: ProgramBundleEntity
} & React.ComponentProps<"div">

export function ProgramBundlePriceCard({ programBundle, ...props }: ProgramBundlePriceCardProps) {
  const { isAuthor } = useProgramBundleRights(programBundle.id)
  const { programBundleStripeSubscriptionInfos } = useProgramBundleAthleteSubscription(programBundle)
  const shouldDisplayImpossiblePurchase =
    programBundle.owner.stripeAccountInError &&
    programBundle.price?.value &&
    programBundle.price.value > 0 &&
    (programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE ||
      programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)

  return (
    <div className="flex flex-col" {...props}>
      {!isAuthor && <ProgramBundleTrialCard programBundle={programBundle} />}

      {programBundleStripeSubscriptionInfos?.error && (
        <div className="mb-3">
          <StripeAccountErrorItem
            orientation="vertical"
            alertTitle={word("global.stripeError.paymentProblem.title")}
            alertSubtitle={getPaymentErrorSubtitle(programBundleStripeSubscriptionInfos.error)}
            alertContent={getPaymentErrorContent(programBundleStripeSubscriptionInfos.error)}
            primaryButton={
              <StripeErrorProgramBundleCardSettingsButton
                programBundleId={programBundle.id}
                paymentError={programBundleStripeSubscriptionInfos.error}
              />
            }
          />
        </div>
      )}

      {/* Impossible purchase information */}
      {shouldDisplayImpossiblePurchase && (
        <div className="card banner flex items-center gap-4 rounded-4xl px-6 py-7">
          <div>
            <ForbiddenAlertIcon
              color="var(--neutral-900)"
              backgroundColor="var(--primary-500)"
              width={50}
              height={50}
            />
          </div>

          <div className="flex flex-col">
            <Text fontStyle="TM">{word("program.price.impossiblePurchase.title")}</Text>
            <Text fontStyle="BM">{word("program.price.impossiblePurchase.content")}</Text>
          </div>
        </div>
      )}

      <div className="card relative flex flex-col gap-2 overflow-hidden rounded-4xl">
        {programBundle.price?.value ? (
          <div className="z-2 flex w-full flex-col gap-2">
            {programBundle.type === ProgramBundleType.VIRTUAL ? (
              <>
                <Text fontStyle="TM">{word("programBundle.price.membership")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundle.price} />
                  <div className="flex">
                    <Text fontStyle="LL">{word("program.price.pricePerMonth")}</Text>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Text fontStyle="TM">{word("program.price.singlePurchase")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundle.price} />
                  <div className="flex">
                    <Text fontStyle="LL">{word("program.price.onlyOnce")}</Text>
                  </div>
                </div>
              </>
            )}

            <Text fontStyle="BM">{word("programBundle.price.payingForAll")}</Text>
          </div>
        ) : (
          <div className="z-2 flex w-full flex-col gap-2">
            <Text fontStyle="TM">{word("program.price.free")}</Text>
            <Text fontStyle="BM">{word("programBundle.price.freeForAll")}</Text>
          </div>
        )}

        {isAuthor && programBundle.trialDuration && (
          <Text fontStyle="BM" className="z-2">
            {`${word("programBundle.price.trial.description", { count: programBundle.trialDuration })}`}
          </Text>
        )}

        {!isAuthor && (
          <div className="z-2 w-full">
            <ProgramBundleSubscriptionActions programBundle={programBundle} />
          </div>
        )}
        <HustleupSmallIcon
          className="absolute top-0 -right-10 z-1 h-full bg-cover bg-center bg-no-repeat"
          color="var(--neutral-100)"
        />
      </div>
    </div>
  )
}
